import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Tooltip,
  Spin
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  SearchOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  LockOutlined
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { apiService, User, Entitlement } from '../services/apiService';
import './UserManagement.css';

const { Search } = Input;
const { Option } = Select;

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [entitlement, setEntitlement] = useState<Entitlement | null>(null);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [form] = Form.useForm();
  const { isAdmin } = useAuth();

  useEffect(() => {
    if (isAdmin) {
      loadData();
    }
  }, [isAdmin]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersData, entitlementData] = await Promise.all([
        apiService.getUsers(),
        apiService.getEntitlement()
      ]);

      const nonAdmins = (usersData || []).filter((u: User) => u.role !== 'admin');
      setUsers(nonAdmins);
      setFilteredUsers(nonAdmins);
      setEntitlement(entitlementData);
    } catch (error: any) {
      console.error('Error loading data:', error);
      message.error(error?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    form.setFieldsValue(user);
    setModalVisible(true);
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      await apiService.deleteUser(userId);
      message.success('User deleted successfully');
      loadData();
    } catch (error: any) {
      console.error('Error deleting user:', error);
      message.error(error?.message || 'Failed to delete user');
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      if (editingUser) {
        await apiService.updateUser(editingUser.userId, values);
        message.success('User updated successfully');
      } else {
        await apiService.createUser(values);
        message.success('User created successfully');
      }

      setModalVisible(false);
      loadData();
    } catch (error: any) {
      console.error('Error saving user:', error);
      message.error(error?.message || 'Failed to save user');
    }
  };

  const handleSearch = (value: string) => {
    if (!value) {
      setFilteredUsers(users);
      return;
    }

    const filtered = users.filter(user =>
      user.firstName.toLowerCase().includes(value.toLowerCase()) ||
      user.lastName.toLowerCase().includes(value.toLowerCase()) ||
      user.email.toLowerCase().includes(value.toLowerCase())
    );

    setFilteredUsers(filtered);
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'red';
      case 'assessor': return 'blue';
      case 'viewer': return 'green';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'green';
      case 'inactive': return 'red';
      default: return 'default';
    }
  };

  const columns = [
    {
      title: 'Name',
      key: 'name',
      render: (_: any, record: User) => `${record.firstName} ${record.lastName}`,
      sorter: (a: User, b: User) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: (a: User, b: User) => a.email.localeCompare(b.email),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => (
        <Tag color={getRoleColor(role)}>
          {role.toUpperCase()}
        </Tag>
      ),
      filters: [
        { text: 'Admin', value: 'admin' },
        { text: 'Assessor', value: 'assessor' },
        { text: 'Viewer', value: 'viewer' },
      ],
      onFilter: (value: any, record: User) => record.role === value,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {status.toUpperCase()}
        </Tag>
      ),
      filters: [
        { text: 'Active', value: 'active' },
        { text: 'Inactive', value: 'inactive' },
      ],
      onFilter: (value: any, record: User) => record.status === value,
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString(),
      sorter: (a: User, b: User) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: User) => (
        <Space>
          <Tooltip title="Edit User">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditUser(record)}
            />
          </Tooltip>

          <Popconfirm
            title="Are you sure you want to delete this user?"
            onConfirm={() => handleDeleteUser(record.userId)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete User">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (!isAdmin) {
    return (
      <div className="um-root">
        <div className="um-denied">
          <div className="um-denied-icon"><LockOutlined /></div>
          <div className="um-denied-title">Access Denied</div>
          <div className="um-denied-desc">You don't have permission to access this page.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="um-root">

      {/* Header */}
      <div className="um-header">
        <div className="um-header-left">
          <div className="um-header-icon"><TeamOutlined /></div>
          <div>
            <h1 className="um-title">User Management</h1>
            <p className="um-subtitle">Manage your team members and their roles</p>
          </div>
        </div>
        <button
          className="um-add-btn"
          onClick={handleCreateUser}
          disabled={
            entitlement
              ? (!entitlement.canCreateUser || entitlement.currentUsers >= entitlement.maxUsers)
              : true
          }
          title={
            entitlement && entitlement.currentUsers >= entitlement.maxUsers
              ? `User quota reached (${entitlement.currentUsers}/${entitlement.maxUsers})`
              : entitlement && !entitlement.canCreateUser
              ? 'Cannot create user - quota limit reached'
              : 'Add User'
          }
        >
          <PlusOutlined /> Add User
        </button>
      </div>

      {/* Stats Row */}
      {entitlement && (
        <div className="um-stats-row">
          <div className="um-stat-card">
            <div className="um-stat-icon blue"><UserOutlined /></div>
            <div className="um-stat-info">
              <div className="um-stat-label">Active Users</div>
              <div className="um-stat-value">
                {entitlement.currentUsers}<span style={{ fontSize: 14, fontWeight: 500, color: '#94a3b8' }}> / {entitlement.maxUsers}</span>
              </div>
            </div>
          </div>
          <div className="um-stat-card">
            <div className="um-stat-icon green"><CheckCircleOutlined /></div>
            <div className="um-stat-info">
              <div className="um-stat-label">Available Slots</div>
              <div className={`um-stat-value ${entitlement.canCreateUser ? 'green' : 'danger'}`}>
                {entitlement.maxUsers - entitlement.currentUsers}
              </div>
            </div>
          </div>
          <div className="um-stat-card">
            <div className="um-stat-icon indigo"><TeamOutlined /></div>
            <div className="um-stat-info">
              <div className="um-stat-label">Status</div>
              <div className={`um-stat-value ${entitlement.status === 'active' ? 'green' : 'danger'}`}>
                {entitlement.status === 'active' ? 'Active' : entitlement.status}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Table Card */}
      <div className="um-table-card">
        <div className="um-table-toolbar">
          <div className="um-search-wrap">
            <Search
              placeholder="Search users by name or email..."
              onSearch={handleSearch}
              allowClear
              prefix={<SearchOutlined />}
            />
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={filteredUsers}
          rowKey="userId"
          loading={loading}
          scroll={{ x: 800 }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`,
          }}
        />
      </div>

      {/* Modal */}
      <Modal
        title={editingUser ? 'Edit User' : 'Create User'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        width={600}
        className="um-modal"
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            role: 'assessor',
            status: 'active',
          }}
        >
          <Form.Item
            name="firstName"
            label="First Name"
            rules={[{ required: true, message: 'Please enter first name' }]}
          >
            <Input placeholder="Enter first name" />
          </Form.Item>

          <Form.Item
            name="lastName"
            label="Last Name"
            rules={[{ required: true, message: 'Please enter last name' }]}
          >
            <Input placeholder="Enter last name" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter valid email' }
            ]}
          >
            <Input placeholder="Enter email address" disabled={!!editingUser} />
          </Form.Item>

          {!editingUser && (
            <Form.Item
              name="password"
              label="Password"
              rules={[
                { required: true, message: 'Please enter a password' },
                { min: 8, message: 'Password must be at least 8 characters' },
              ]}
            >
              <Input.Password placeholder="Enter password for this user" />
            </Form.Item>
          )}

          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: 'Please select role' }]}
          >
            <Select placeholder="Select role">
              <Option value="admin">Admin</Option>
              <Option value="assessor">Assessor</Option>
              <Option value="viewer">Viewer</Option>
            </Select>
          </Form.Item>

          {editingUser && (
            <Form.Item
              name="status"
              label="Status"
              rules={[{ required: true, message: 'Please select status' }]}
            >
              <Select placeholder="Select status">
                <Option value="active">Active</Option>
                <Option value="inactive">Inactive</Option>
              </Select>
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default UserManagement;
